import { useQuery } from '@tanstack/react-query';
import type { WhatsAppInboxChat } from '@zrsystem/contracts';

import { useWorkspace } from '@/app/workspace/use-workspace.js';
import {
  isMissingWhatsAppAccount,
  listWhatsAppInboxChats,
} from '@/features/notification/api/whatsapp-inbox.api.js';
import { overlayWhatsAppInboxChatUnread } from '@/features/notification/lib/whatsapp-inbox-unread.js';
import { notificationQueryKeys } from '@/features/notification/query-keys.js';

export interface WhatsAppInboxChatsResult {
  readonly chats: WhatsAppInboxChat[];
  readonly accountUnavailable: boolean;
}

export function sortWhatsAppInboxChats(chats: readonly WhatsAppInboxChat[]): WhatsAppInboxChat[] {
  return [...chats].sort((left, right) => {
    const leftTs = left.lastMessage?.timestamp ?? 0;
    const rightTs = right.lastMessage?.timestamp ?? 0;
    return rightTs - leftTs;
  });
}

export function sumWhatsAppInboxUnreadCount(chats: readonly WhatsAppInboxChat[]): number {
  return chats.reduce((total, chat) => total + chat.unreadCount, 0);
}

export function useWhatsAppInboxChats(placeId: string) {
  return useQuery({
    queryKey: notificationQueryKeys.whatsappInboxChats(placeId),
    queryFn: async (): Promise<WhatsAppInboxChatsResult> => {
      try {
        const response = await listWhatsAppInboxChats(placeId, { limit: 100 });
        return {
          chats: sortWhatsAppInboxChats(
            response.chats.map((chat) => overlayWhatsAppInboxChatUnread(placeId, chat)),
          ),
          accountUnavailable: false,
        };
      } catch (error) {
        if (isMissingWhatsAppAccount(error)) {
          return { chats: [], accountUnavailable: true };
        }
        throw error;
      }
    },
    enabled: placeId.length > 0,
  });
}

export function useWhatsAppInboxChatsForActivePlace() {
  const { activePlace } = useWorkspace();
  const placeId = activePlace?.id ?? '';
  return useWhatsAppInboxChats(placeId);
}
